import React from "react";
import { useNavigate } from "react-router-dom";
import { api, ASSET_URL } from "../../lib/api";
import { toast } from "sonner";
import { Save, Image as ImageIcon, X, Sparkles, GripVertical, CalendarPlus } from "lucide-react";
// RichTextEditor im News-Admin bewusst deaktiviert: stabile HTML-Textareas statt TipTap
import MediaPickerModal from "../../components/admin/MediaPickerModal";

const todayIso = () => `${new Date().toISOString().slice(0, 10)}T12:00:00+00:00`;
const empty = { slug: "", title_de: "", title_en: "", excerpt_de: "", excerpt_en: "", body_de: "", body_en: "", cover_media_id: null, video_media_id: null, poster_media_id: null, published: true, published_at: todayIso() };

const slugify = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/ä/g, "ae")
  .replace(/ö/g, "oe")
  .replace(/ü/g, "ue")
  .replace(/ß/g, "ss")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

export default function NewsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = React.useState([]);
  const [draft, setDraft] = React.useState({ ...empty });

  const bodyDeRef = React.useRef(null);
  const bodyEnRef = React.useRef(null);

  const escapeArticleCtaHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  const normalizeArticleCtaUrl = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith("mailto:") || raw.startsWith("tel:")) return raw;
    return `https://${raw}`;
  };

  const insertArticleCta = (field, ref, event) => {
    if (event && typeof event.preventDefault === "function") event.preventDefault();

    const text = window.prompt("Spruch über dem Button:");
    if (!text) return;

    const label = window.prompt("Button-Text:", "Jetzt ansehen");
    if (label === null) return;

    const url = window.prompt("Externe URL, z. B. https://...");
    if (!url) return;

    const cleanUrl = url.trim();
    const safeUrl = /^https?:\/\//i.test(cleanUrl) ? cleanUrl : `https://${cleanUrl}`;

    const ctaHtml = `

<div class="drb-article-cta">
  <p>${escapeArticleCtaHtml(text)}</p>
  <a href="${escapeArticleCtaHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${escapeArticleCtaHtml(label || "Jetzt ansehen")}</a>
</div>

`;

    const el = ref?.current;
    const current = el && typeof el.value === "string" ? el.value : (draft?.[field] || "");
    const start = el && typeof el.selectionStart === "number" ? el.selectionStart : current.length;
    const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : start;
    const nextValue = current.slice(0, start) + ctaHtml + current.slice(end);

    // Sofort in React-State schreiben, damit der kontrollierte textarea-Wert aktualisiert wird.
    setDraft(prev => ({
      ...prev,
      [field]: nextValue
    }));

    // Zusätzlich DOM-Wert setzen und Cursor hinter den CTA setzen. Das macht die Änderung sofort sichtbar.
    window.setTimeout(() => {
      const target = ref?.current;
      if (!target) return;
      target.focus();
      try {
        target.value = nextValue;
        const pos = start + ctaHtml.length;
        target.selectionStart = pos;
        target.selectionEnd = pos;
      } catch (_) {}
    }, 0);
  };
  const [editId, setEditId] = React.useState(null);
  const [coverAsset, setCoverAsset] = React.useState(null);
  const formRef = React.useRef(null);
  const [picker, setPicker] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [aiBusy, setAiBusy] = React.useState(false);
  const [draggedId, setDraggedId] = React.useState(null);
  const [reordering, setReordering] = React.useState(false);

  const load = () => api.adminNews().then(setItems);
  React.useEffect(() => { load(); }, []);

  const resolveCover = async (id) => {
    if (!id) { setCoverAsset(null); return; }
    const all = await api.adminMedia();
    setCoverAsset(all.find(m => m.id === id) || null);
  };

  const save = async () => {
    if (!draft.slug || !draft.title_de) { toast.error("Slug und Titel sind nötig"); return; }
    setSaving(true);
    try {
      const saved = editId ? await api.updateNews(editId, draft) : await api.createNews(draft);
      toast.success("Gespeichert — Übersetzung und SEO/GEO sind jetzt direkt verfügbar");
      setDraft({ ...empty, ...saved });
      setEditId(saved.id);
      resolveCover(saved.cover_media_id);
      load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Beitrag löschen?")) return; await api.deleteNews(id); load(); };
  const duplicate = async (id) => {
    try {
      const copy = await api.duplicateNews(id);
      toast.success("Beitrag als Entwurf dupliziert");
      await load();
      edit(copy);
    } catch {
      toast.error("Beitrag konnte nicht dupliziert werden");
    }
  };
  const moveNews = async (targetId) => {
    if (!draggedId || draggedId === targetId || reordering) return;
    const next = [...items];
    const from = next.findIndex(item => item.id === draggedId);
    const to = next.findIndex(item => item.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setDraggedId(null);
    setReordering(true);
    try {
      await api.reorderNews(next.map(item => item.id));
      toast.success("Reihenfolge gespeichert");
    } catch {
      toast.error("Reihenfolge konnte nicht gespeichert werden");
      load();
    } finally {
      setReordering(false);
    }
  };
  const edit = (n) => {
    setDraft({ ...empty, ...n });
    setEditId(n.id);
    resolveCover(n.cover_media_id);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const autoTranslateNews = async () => {
    if (!editId) { toast.error("Bitte Beitrag zuerst speichern, dann übersetzen."); return; }
    setAiBusy(true);
    try {
      const updated = await api.translateNews(editId);
      setDraft({ ...empty, ...updated });
      resolveCover(updated.cover_media_id);
      toast.success("News-Beitrag übersetzt");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Übersetzung fehlgeschlagen");
    } finally {
      setAiBusy(false);
    }
  };

  const generateNewsSeo = async (language = "de") => {
    if (!editId) { toast.error("Bitte Beitrag zuerst speichern, dann SEO/GEO/KI erzeugen."); return; }
    setAiBusy(true);
    try {
      const updated = await api.generateNewsSeo(editId, language);
      setDraft({ ...empty, ...updated });
      resolveCover(updated.cover_media_id);
      toast.success("SEO/GEO/KI erzeugt");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "SEO/GEO/KI konnte nicht erzeugt werden");
    } finally {
      setAiBusy(false);
    }
  };

  const createEventFromNews = async () => {
    if (!editId) { toast.error("Bitte den News-Beitrag zuerst speichern."); return; }
    setAiBusy(true);
    try {
      const event = await api.createEventFromNews(editId);
      toast.success("Veranstaltungsentwurf mit Englisch und SEO/GEO erstellt");
      navigate(`/admin/events?edit=${encodeURIComponent(event.id)}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Veranstaltungsentwurf konnte nicht erstellt werden");
    } finally {
      setAiBusy(false);
    }
  };

  const ogPreview = (n) => n.cover_media_id ? null : (n.og_media_id ? `/api/public/media/file/webp/og_news_${n.id}.webp` : null);

  return (
    <div data-testid="admin-news">
      <div className="mb-6"><div className="brut-tag mb-3">CMS / AKTUELLES</div><h1 className="brut-h2">News / Fachbeiträge</h1>
        <p className="brut-body mt-2 opacity-75 text-sm">Beim Speichern werden automatisch KI-Schlagwörter extrahiert und ein Share-Bild erzeugt (oder dein gewähltes Titelbild genutzt).</p>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5 space-y-3">
          <p className="text-xs opacity-65 mb-2">Am grünen Griff ziehen, um Beiträge nach oben oder unten zu verschieben.</p>
          {items.map(n => (
            <div key={n.id} draggable={!reordering} onDragStart={e => { setDraggedId(n.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", n.id); }} onDragEnd={() => setDraggedId(null)} onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }} onDrop={() => moveNews(n.id)} className={`relative border-2 border-[var(--ink)] p-4 pl-11 transition-opacity ${draggedId === n.id ? "opacity-45" : "opacity-100"}`} data-testid={`news-item-${n.id}`}>
              <button type="button" className="absolute left-2 top-1/2 -translate-y-1/2 w-7 min-h-16 rounded-full bg-emerald-600 text-white flex items-center justify-center cursor-grab active:cursor-grabbing" aria-label={`${n.title_de} verschieben`} title="Ziehen zum Verschieben"><GripVertical size={18}/></button>
              <div className="font-mono text-[10px] uppercase opacity-60">/wissen/news/{n.slug} {n.published ? "" : "· (Entwurf)"}</div>
              <div className="font-mono text-[10px] uppercase opacity-60 mt-1">Veröffentlicht: {(n.published_at || "").slice(0, 10) || "—"}</div>
              <div className="brut-h3 mt-1">{n.title_de}</div>
              {(n.keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {n.keywords.map((k, i) => <span key={i} className="font-mono text-[10px] uppercase bg-[var(--ink)] text-[var(--bg)] px-2 py-0.5">{k}</span>)}
                </div>
              )}
              <div className="flex gap-3 mt-3">
                <button type="button" onClick={() => edit(n)} className="font-mono text-xs underline" data-testid={`news-edit-${n.id}`}>Edit</button>
                <button type="button" onClick={() => remove(n.id)} className="font-mono text-xs underline text-red-600">Löschen</button>
                <button type="button" onClick={() => duplicate(n.id)} className="font-mono text-xs underline">Duplizieren</button>
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="border-2 border-[var(--ink)] p-4 space-y-3">
            <div className="font-mono text-xs uppercase tracking-[0.2em]">{editId ? "Beitrag bearbeiten" : "Neuer Beitrag"}</div>
            <button type="button" onClick={createEventFromNews} disabled={saving || aiBusy || !editId} className="brut-btn !py-3 !px-4 text-sm w-full sm:w-auto" data-testid="news-create-event">
              <CalendarPlus size={17} /> {aiBusy ? "KI erstellt Veranstaltungsentwurf…" : "Aus diesem Artikel eine Veranstaltung erstellen"}
            </button>
            {!editId && <div className="text-xs opacity-60">Artikel zuerst speichern – danach wird dieser Button aktiv.</div>}
            <label className="block">
              <span className="brut-label">Veröffentlichungsdatum</span>
              <input type="date" className="brut-input" value={(draft.published_at || "").slice(0, 10)} onChange={e => setDraft({ ...draft, published_at: e.target.value ? `${e.target.value}T12:00:00+00:00` : todayIso() })} data-testid="news-published-at" />
              <small className="block mt-1 opacity-60">Dieses Datum wird öffentlich am Beitrag angezeigt. Die Position links kann zusätzlich per Drag-and-drop festgelegt werden.</small>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input className="brut-input" placeholder="Slug" value={draft.slug} onChange={e => setDraft({ ...draft, slug: e.target.value })} data-testid="news-slug" />
              <label className="flex items-center gap-2 text-xs font-mono uppercase"><input type="checkbox" checked={draft.published} onChange={e => setDraft({ ...draft, published: e.target.checked })} /> Veröffentlicht</label>
            </div>
            <input className="brut-input" placeholder="Titel (DE)" value={draft.title_de} onChange={e => {
              const title = e.target.value;
              const oldAutoSlug = slugify(draft.title_de);
              const keepAutomatic = !draft.slug || draft.slug === oldAutoSlug;
              setDraft({ ...draft, title_de: title, slug: keepAutomatic ? slugify(title) : draft.slug });
            }} data-testid="news-title-de" />
            <textarea rows={2} className="brut-textarea" placeholder="Teaser (DE)" value={draft.excerpt_de} onChange={e => setDraft({ ...draft, excerpt_de: e.target.value })} />

            {/* Cover / Share image */}
            <div className="border border-[var(--line-soft)] p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60 mb-2">Titel-/Share-Bild (optional — sonst automatisches Markenbild)</div>
              {coverAsset ? (
                <div className="flex items-center gap-3">
                  <img src={ASSET_URL(coverAsset.variants?.thumb || coverAsset.variants?.webp)} alt="" className="w-24 h-16 object-cover border border-[var(--ink)]" />
                  <button type="button" onClick={() => { setDraft({ ...draft, cover_media_id: null }); setCoverAsset(null); }} className="font-mono text-[11px] underline text-red-600 inline-flex items-center gap-1"><X size={11} /> Entfernen</button>

                </div>
              ) : (
                <button type="button" onClick={() => setPicker({ field: "cover_media_id", type: "image" })} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid="news-cover-pick"><ImageIcon size={12} /> Bild wählen</button>
              )}
            </div>

            <div>
              <label className="brut-label">Inhalt (DE)</label>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="mt-6 mb-6 border-2 border-[var(--ink)] rounded-[22px] p-4 bg-white/60" data-testid="news-video-admin-block">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-60 mb-3">Artikel-Video</div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-[var(--ink)] rounded-[16px] p-4 bg-[var(--bg)]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-55 mb-2">MP4 / Video</div>
                    <div className="text-xs break-all mb-3 opacity-70">{draft.video_media_id || "Kein Video ausgewählt"}</div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => setPicker({ field: "video_media_id", type: "video" })} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs">Video auswählen</button>
                      {draft.video_media_id && <button type="button" onClick={() => setDraft({ ...draft, video_media_id: null })} className="font-mono text-[11px] underline text-red-600">Entfernen</button>}
                    </div>
                  </div>

                  <div className="border border-[var(--ink)] rounded-[16px] p-4 bg-[var(--bg)]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-55 mb-2">Vorschaubild / Poster</div>
                    <div className="text-xs break-all mb-3 opacity-70">{draft.poster_media_id || "Kein Vorschaubild ausgewählt"}</div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => setPicker({ field: "poster_media_id", type: "image" })} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs">Vorschaubild auswählen</button>
                      {draft.poster_media_id && <button type="button" onClick={() => setDraft({ ...draft, poster_media_id: null })} className="font-mono text-[11px] underline text-red-600">Entfernen</button>}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-[11px] opacity-60 leading-snug">
                  Das Vorschaubild erscheint als Poster im Player. Hochformatige und querformatige MP4-Dateien werden responsive angezeigt.
                </div>
              </div>

                <button
                  type="button"
                  className="brut-btn !py-2 !px-4 text-xs"

                  onClick={(e) => insertArticleCta("body_de", bodyDeRef, e)}
                  data-testid="insert-cta-de"
                >
                  CTA einfügen
                </button>
                <span className="text-xs opacity-60">setzt den CTA an die aktuelle Cursorposition</span>
              </div>
<textarea
                ref={bodyDeRef}
                className="brut-input min-h-[260px] font-mono text-xs leading-relaxed"
                value={draft.body_de || ""}
                onChange={e => setDraft({ ...draft, body_de: e.target.value })}
                placeholder="<p>Deutscher Beitragstext...</p>"
                data-testid="news-body-de"
              />
            </div>

            <details>
              <summary className="font-mono text-xs uppercase cursor-pointer opacity-70">Englische Version (optional)</summary>
              <div className="space-y-3 mt-3">
                <input className="brut-input" placeholder="Title (EN)" value={draft.title_en || ""} onChange={e => setDraft({ ...draft, title_en: e.target.value })} />
                <textarea rows={2} className="brut-textarea" placeholder="Excerpt (EN)" value={draft.excerpt_en || ""} onChange={e => setDraft({ ...draft, excerpt_en: e.target.value })} />
                              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                  type="button"
                  className="brut-btn !py-2 !px-4 text-xs"
                  onClick={(e) => insertArticleCta("body_en", bodyEnRef, e)}
                  data-testid="insert-cta-en"
                >
                  Insert CTA
                </button>
                <span className="text-xs opacity-60">setzt den CTA an die aktuelle Cursorposition</span>
              </div>
<textarea
                ref={bodyEnRef}
                className="brut-input min-h-[260px] font-mono text-xs leading-relaxed"
                value={draft.body_en || ""}
                onChange={e => setDraft({ ...draft, body_en: e.target.value })}
                placeholder="<p>Englischer Beitragstext...</p>"
                data-testid="news-body-en"
              />
              </div>
            </details>

            <div className="border border-[var(--line-soft)] p-3 space-y-2" data-testid="news-ai-tools">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60">KI / Übersetzung / SEO</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={autoTranslateNews} disabled={saving || aiBusy || !editId} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid="news-ai-translate">
                  <Sparkles size={12} /> {aiBusy ? "KI arbeitet…" : "Auto-Übersetzen DE → EN"}
                </button>
                <button type="button" onClick={() => generateNewsSeo("de")} disabled={saving || aiBusy || !editId} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid="news-ai-seo">
                  <Sparkles size={12} /> SEO / GEO / KI erzeugen
                </button>
              </div>
              {!editId && <div className="font-mono text-[10px] opacity-50">Beitrag zuerst speichern, danach sind die KI-Funktionen aktiv.</div>}
              {draft.seo_de && (
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {draft.seo_de.title && <input className="brut-input text-xs" value={draft.seo_de.title || ""} readOnly />}
                  {draft.seo_de.description && <textarea rows={2} className="brut-textarea text-xs" value={draft.seo_de.description || ""} readOnly />}
                  {draft.seo_de.ai_summary && <textarea rows={2} className="brut-textarea text-xs" value={draft.seo_de.ai_summary || ""} readOnly />}
                </div>
              )}
            </div>

            {draft.keywords && draft.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 items-center"><Sparkles size={12} className="opacity-50" />{draft.keywords.map((k, i) => <span key={i} className="font-mono text-[10px] uppercase bg-black/5 px-2 py-0.5">{k}</span>)}</div>
            )}

            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={saving || aiBusy} className="brut-btn !py-2 !px-3 text-xs" data-testid="news-save"><Save size={12} /> {saving ? "Speichert & analysiert…" : "Speichern"}</button>
              {editId && <button type="button" onClick={() => { setDraft({ ...empty }); setEditId(null); setCoverAsset(null); }} className="font-mono text-xs underline">Abbrechen</button>}
            </div>
          </div>
        </div>
      </div>
      {picker && <MediaPickerModal
        filter={picker.type || "image"}
        onSelect={(a) => {
          const field = picker.field || "cover_media_id";
          setDraft(d => ({ ...d, [field]: a.id }));
          if (field === "cover_media_id") setCoverAsset(a);
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />}
    </div>
  );
}
